<script setup>
import { nextTick, watch, ref } from 'vue';
import { api } from '../api.js';
import { useVault, people, ALL } from '../stores/vault.js';

const vault = useVault();
const rail = ref(null);

// Making a collection used to mean going to People and scrolling. It is the
// thing you want at the moment you realise the current one is wrong, so it
// belongs next to the collections themselves.
const making = ref(false);
const name = ref('');
const colour = ref('#4db3a8');
const visibility = ref('selected');

const WHO = [
  ['everyone', 'Everyone in the org', 'Every account here can read it straight away.'],
  ['selected', 'Just some people', 'Only you to start with. Add people on the People pane.'],
  ['private', 'Keep it to myself', 'Yours alone, permanently. Nobody can be added.'],
];
const busy = ref(false);
const error = ref('');

const PALETTE = ['#4db3a8', '#1e3a3a', '#d4a843', '#c45b5b', '#5bc5e8', '#8a9e9e'];

async function create() {
  const display = name.value.trim();
  if (!display || busy.value) return;
  busy.value = true;
  error.value = '';
  try {
    const made = await api.createCollection({
      name: display, colour: colour.value, visibility: visibility.value,
    });
    await vault.refreshCollections();
    vault.select(made.id);
    name.value = '';
    making.value = false;
    vault.say(`Created ${made.name}. Everything below is scoped to it now.`);
  } catch (err) {
    error.value = err.message;
  } finally {
    busy.value = false;
  }
}

// The selected chip is the answer to "where does this go", so keep it visible.
watch(() => vault.currentId, async () => {
  await nextTick();
  rail.value?.querySelector('[aria-pressed="true"]')
    ?.scrollIntoView({ inline: 'center', block: 'nearest' });
}, { immediate: true });

function pick(c) {
  if (c.id === vault.currentId) return;
  vault.select(c.id);
  vault.say(`Now in ${c.name}`);
}
</script>

<template>
  <!-- The scope control. It is never hidden, because everything below it is
       scoped by whatever is selected here. -->
  <div class="spacebar">
    <div class="shell">
      <div class="spacerow">
        <span class="spacelab">Collection</span>
        <div ref="rail" class="spaces">
          <button
            class="space allspace" :aria-pressed="vault.currentId === ALL"
            @click="pick({ id: ALL, name: 'All Collections' })"
          >
            <i class="allswatch" />All Collections
          </button>
          <button
            v-for="c in vault.collections" :key="c.id"
            class="space" :aria-pressed="c.id === vault.currentId"
            @click="pick(c)"
          >
            <i class="swatch" :style="{ background: c.colour }" />{{ c.name }}
          </button>
          <button class="space newspace" title="New collection" @click="making = !making">
            <span aria-hidden="true">+</span> New
          </button>
        </div>
      </div>

      <form v-if="making" class="newform" @submit.prevent="create">
        <input
          v-model="name" class="inp" placeholder="Northwind, Playbooks, Q3 audit"
          aria-label="New collection name" autofocus
        >
        <div class="swatches" role="group" aria-label="Colour">
          <button
            v-for="p in PALETTE" :key="p" type="button" class="pick"
            :class="{ on: colour === p }" :style="{ background: p }"
            :aria-label="p" :aria-pressed="colour === p" @click="colour = p"
          />
        </div>
        <fieldset class="who">
          <legend class="de-label">Who can see it</legend>
          <label v-for="[value, label, hint] in WHO" :key="value" class="wholabel">
            <input v-model="visibility" type="radio" :value="value">
            <span><b>{{ label }}</b><small>{{ hint }}</small></span>
          </label>
        </fieldset>
        <button class="de-btn" :disabled="busy || !name.trim()">
          {{ busy ? 'Creating' : 'Create' }}
        </button>
        <button type="button" class="mini" @click="making = false; error = ''">Cancel</button>
        <span v-if="error" class="newerror" role="alert">{{ error }}</span>
      </form>
      <div v-if="vault.isAll" class="wholine">
        <svg class="lock" viewBox="0 0 24 24"><circle cx="11" cy="11" r="7" /><path d="M20 20l-4-4" /></svg>
        Everything you can reach: <b>{{ vault.collections.length }}</b>
        {{ vault.collections.length === 1 ? 'collection' : 'collections' }}.
        Pick one to add to it.
      </div>
      <div v-else-if="vault.current" class="wholine">
        <svg class="lock" viewBox="0 0 24 24">
          <rect x="4" y="10" width="16" height="10" /><path d="M8 10V7a4 4 0 0 1 8 0v3" />
        </svg>
        <template v-if="vault.current.isPrivate">
          Private to you. Nobody else can search this.
        </template>
        <template v-else>
          <b>{{ people(vault.current.people) }}</b> can search here.
          {{ vault.current.note }}
        </template>
      </div>
    </div>
  </div>
</template>

<style scoped>
.allswatch {
  width: 10px;
  height: 10px;
  flex: none;
  border: 1px solid rgba(30, 58, 58, .35);
  background: linear-gradient(135deg, #1e3a3a 0 33%, #4db3a8 33% 66%, #d4a843 66% 100%);
}
.allspace[aria-pressed="true"] .allswatch { border-color: rgba(244, 241, 232, .5); }
.newspace {
  border-style: dashed;
  font-family: var(--de-mono);
  font-size: 12px;
  letter-spacing: .08em;
  text-transform: uppercase;
  gap: 4px;
}
.newform {
  display: flex;
  gap: 8px;
  align-items: center;
  flex-wrap: wrap;
  margin-top: 10px;
  padding-top: 10px;
  border-top: 1px dashed var(--de-border-strong, #9e9b92);
}
.newform .inp { flex: 1; min-width: 200px; width: auto; }
.swatches { display: flex; gap: 4px; }
.pick {
  width: 26px;
  height: 26px;
  border: 1px solid var(--de-border-strong, #9e9b92);
  cursor: pointer;
  padding: 0;
}
.pick.on { outline: 2px solid var(--de-ink); outline-offset: 1px; }
.who {
  border: 1px solid var(--de-border-strong, #c8c5bc);
  padding: 8px 10px;
  margin: 0;
  display: flex;
  gap: 14px;
  flex-wrap: wrap;
  width: 100%;
}
.who legend { padding: 0 4px; }
.wholabel { display: flex; gap: 6px; align-items: flex-start; cursor: pointer; font-size: var(--de-t-sm); }
.wholabel small { display: block; color: var(--de-ink-faint); font-size: 11px; }
.newerror { font-size: var(--de-t-sm); color: var(--de-signal-err); width: 100%; }
</style>
